import {Graphics} from "pixi.js";
import {inches, lineBlueHeavy} from "../const";
import {HardwareCategoryFields, hasCategory} from "./hardwareGlyphs/HardwareGlyph";

// a door sweep seen face-on, for cosmetics only: the retainer strip screwed along the
// bottom of the face, and the rubber blade below it that hangs into the undercut. Fixed
// size and place (it is not a placed item), drawn when the opening's hardware group has
// a sweep (see hasSweep).
const RETAINER_HEIGHT = 1.25 * inches;
const BLADE_DROP = 0.5 * inches;
const EDGE_INSET = 0.5 * inches;
const OUTLINE = 0.25 * inches;
const RUBBER_GREY = 0x4A4A4A;

// seen from the latch edge: the retainer flange standing off the face, and the blade
// below it. Drawn heavier than real so they show at drawing scale.
const FLANGE_THICKNESS = 0.2 * inches;
const BLADE_THICKNESS = 0.2 * inches;

// the face a sweep mounts on: the push face.
export const SWEEP_FACE = "push";

// a sweep: anything under the "Sweeps" category family (rubber or brush).
export function isSweep(item: HardwareCategoryFields): boolean {
    return hasCategory(item, "Sweeps", "Rubber Sweep", "Brush Sweep");
}

export function hasSweep(items: HardwareCategoryFields[]): boolean {
    return items.some(isSweep);
}

// draw a sweep along the bottom of a leaf face: `left`/`width` span the leaf, `bottom` is
// the bottom of the face (the top of the undercut), in `g`'s coords.
export function drawDoorSweep(g: Graphics, left: number, bottom: number, width: number) {
    const w = Math.max(0, width - 2 * EDGE_INSET);
    if(w <= 0) return;
    const x = left + EDGE_INSET;

    // the retainer on the face.
    g.lineStyle(OUTLINE, lineBlueHeavy, 0.8);
    g.beginFill(lineBlueHeavy, 0.15);
    g.drawRect(x, bottom - RETAINER_HEIGHT, w, RETAINER_HEIGHT);
    g.endFill();

    // the rubber blade below the door bottom.
    g.lineStyle(0);
    g.beginFill(RUBBER_GREY, 0.85);
    g.drawRect(x, bottom, w, BLADE_DROP);
    g.endFill();
}

// draw a sweep as seen from the latch edge: `faceX` is the face it mounts on, `out` the
// horizontal direction away from that face (out of the door), `bottom` the bottom of the
// face (the top of the undercut), in `g`'s coords.
export function drawDoorSweepEdge(g: Graphics, faceX: number, out: 1 | -1, bottom: number) {
    const flangeX = Math.min(faceX, faceX + out * FLANGE_THICKNESS);
    g.lineStyle(0);
    g.beginFill(lineBlueHeavy, 0.8);
    g.drawRect(flangeX, bottom - RETAINER_HEIGHT, FLANGE_THICKNESS, RETAINER_HEIGHT);
    g.endFill();

    const bladeX = Math.min(faceX, faceX + out * BLADE_THICKNESS);
    g.beginFill(RUBBER_GREY, 0.85);
    g.drawRect(bladeX, bottom, BLADE_THICKNESS, BLADE_DROP);
    g.endFill();
}
